import { Dropdown, Option } from "@fluentui/react-components";
import * as React from "react";
import type { Appearance, FacetDictionary, FacetValue } from "../data";
import { useStyles } from "./PromptFilter.styles";
import { PromptFiltersSkeleton } from "./PromptFilterSkeleton";
import { usePromptFilterLocalizations } from "./usePromptFilterStrings";

export type NoCustomerRelatedFilterDimension = string;
export type NoCustomerRelatedFilterValue = string;

export type PropFiltersProps = {
  /**
   * Optional appearance to be applied to control icon style
   */
  appearance?: Appearance;

  /**
   * The id of the FluentUI tab that's hosting this filter.
   */
  tab: string;

  /**
   * The facets used to generate the filter dropdowns.
   */
  facets: FacetDictionary;

  /**
   * Optional loading state to be applied to the root element. If true, a skeleton view will be rendered.
   */
  isLoading?: boolean;

  /**
   * The currently selected facets.
   */
  selectedFacets: Record<string, string[]>;

  /**
   * Warning! Callback to be called when the filter changes.
   * Warning! Do NOT send customer related data to this function!
   * @param filters The new filters
   * @returns void
   */
  onFilterChanged: (filters: Record<string, string[]>) => void;
};

/*
  Fluentui has an open bug where dropdown texts are not properly truncated.
  The bug causes text overflow that cannot be fixed with the css property
  https://github.com/microsoft/fluentui/issues/27824
  We will remove this function and use css property once the bug is addressed by Fluent.
  Note: this will not fix for all locales, as char length is different for each language.
*/
export const truncateString = (
  inputString: string,
  maxWidth: number,
  fontFamily = "Segoe UI",
  fontSize = 16
): string => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return inputString;
  }
  context.font = `${fontSize}px ${fontFamily}`;

  let currentWidth = 0;

  for (let i = 0; i < inputString.length; i++) {
    const charWidth = context.measureText(inputString[i]).width;

    if (
      currentWidth + charWidth >
      maxWidth - context.measureText("...").width
    ) {
      return inputString.substring(0, i) + "...";
    }

    currentWidth += charWidth;
  }

  return inputString;
};

export const PromptFilters: React.FC<PropFiltersProps> = ({
  appearance,
  tab,
  facets,
  selectedFacets,
  isLoading,
  onFilterChanged,
}) => {
  const styles = useStyles(appearance)();
  const { facetKeyToLabel, facetValueToLabel } = usePromptFilterLocalizations();
  const truncateStringMaxWidth = appearance === "single-col" ? 100 : 180;

  return (
    <div className={styles.root}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <>
          {Object.entries(facets).map(([facetKey, faceValues]) => (
            <Dropdown
              key={`${tab}-${facetKey}`}
              data-testid={`${facetKey}-dropdown`}
              aria-label={facetKeyToLabel(facetKey)}
              className={styles.dropdown}
              multiselect={true}
              placeholder={facetKeyToLabel(facetKey)}
              value={
                selectedFacets[facetKey]
                  ? truncateString(
                      selectedFacets[facetKey]
                        .map((value) => facetValueToLabel(value))
                        .join(", "),
                      truncateStringMaxWidth
                    )
                  : ""
              }
              selectedOptions={selectedFacets[facetKey] ?? []}
              onOptionSelect={(_evt, data) => {
                onFilterChanged({
                  ...selectedFacets,
                  [facetKey]: data.selectedOptions,
                });
              }}
            >
              {faceValues.map((facet: FacetValue) => (
                <Option
                  text={facet.value}
                  value={facet.value}
                  key={facet.value}
                  aria-label={`${facetValueToLabel(facet.value)}-checkbox`}
                >
                  {facetValueToLabel(facet.value)}
                </Option>
              ))}
            </Dropdown>
          ))}
        </>
      )}
    </div>
  );
};
